import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';

export function Spinner({ size = 'md', className }: { size?: 'md' | 'lg'; className?: string }) {
  return <span className={clsx('kts-spinner', size === 'lg' && 'kts-spinner--lg', className)} role="status" aria-label="Yuklanmoqda" />;
}

export function Skeleton({
  width = '100%',
  height = 16,
  radius,
  style,
  className,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  style?: CSSProperties;
  className?: string;
}) {
  return <span className={clsx('kts-skeleton', className)} style={{ display: 'block', width, height, borderRadius: radius, ...style }} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="kts-empty">
      {icon && <div className="kts-empty__icon">{icon}</div>}
      <div className="kts-empty__title">{title}</div>
      {description && <div style={{ maxWidth: 420, fontSize: 13 }}>{description}</div>}
      {action && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  );
}

export function PageLoader({ label = 'Yuklanmoqda…' }: { label?: string }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: 280, gap: 12, color: 'var(--muted)' }}>
      <div style={{ display: 'grid', placeItems: 'center', gap: 12 }}>
        <Spinner size="lg" />
        <span style={{ fontSize: 13 }}>{label}</span>
      </div>
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'warning' ? AlertTriangle : tone === 'danger' ? XCircle : Info;
  return (
    <div className="kts-alert" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontWeight: 700, marginBottom: children ? 2 : 0 }}>{title}</div>}
        {children && <div style={{ color: 'var(--text-2)' }}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Meter({ value, max = 100, tone }: { value: number; max?: number; tone?: 'warning' | 'danger' }) {
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  return (
    <div className="kts-meter" data-tone={tone} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <div className="kts-meter__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
